// ---------------------------------------------------------------------------
// Viz DSL Layout Engine — 5-phase vertical flow layout algorithm.
//
// Extracted from VizDslRenderer.js renderFlow().  Manages child block
// measurement, height allocation, text shrinking, slack expansion, and
// rendering.  Depends on the block registry for per-type layout metadata
// (isContentSized, canStretch, hasData).
//
// The flow block definition is a special container — it doesn't produce
// visible content itself; it positions its children.
// ---------------------------------------------------------------------------

import Konva from "konva";

// During migration: renderBlock is the existing switch-based dispatcher.
// After Phase F wiring, this becomes the registry-based dispatch.
import { renderBlock } from "./VizDslRenderer.js";
import { themeColor } from "./renderers/plainObjectRenderer";

// Registry-based layout queries (wired in Phase F, used now where available)
let _registry = null;
function getRegistry() {
  if (!_registry) {
    try {
      _registry = require("../shared/block-registry/index.js").blockRegistry;
    } catch { /* registry not initialized yet */ }
  }
  return _registry;
}

// ── Layout helpers ───────────────────────────────────────────────────────

/**
 * Does a child block have extracted data worth showing?
 * Uses the registry's hasData if available, otherwise falls back to generic check.
 */
function childHasData(child, crystalizedData) {
  const reg = getRegistry();
  if (reg) return reg.hasData(child.type, child, crystalizedData);

  // Fallback (during migration)
  const d = crystalizedData[child.id];
  if (d == null) return false;
  if (child.type === "chart" && child.kind === "progress") {
    return d.value != null || (typeof d.display_value === "string" && d.display_value.trim() !== "");
  }
  if (Array.isArray(d)) return d.length > 0;
  if (typeof d === "object") return Object.values(d).some(v => v != null && v !== "");
  return d !== "";
}

/**
 * Select visible children for max_visible flows.
 */
function selectVisibleChildren(block, crystalizedData) {
  const children = block.children || [];
  const max = block.max_visible;
  if (!max || children.length <= max) return children;
  const withData = children.filter(c => childHasData(c, crystalizedData));
  const without = children.filter(c => !childHasData(c, crystalizedData));
  return [...withData, ...without].slice(0, max);
}

/**
 * Is this child block content-sized (reports its own natural height)?
 * Uses the registry if available, otherwise falls back to hardcoded list.
 */
function isContentSized(childBlock) {
  // tile_row manages its own internal text sizing; letting it participate in the
  // global Phase 3 shrink would crush ALL sibling text just to fit the tiles.
  if (childBlock.type === "tile_row") return false;
  // A flow with sizing:"content" shrink-wraps its children like a text_block.
  if (childBlock.type === "flow" && childBlock.sizing === "content") return true;
  // progress/heatmap charts have a fixed natural content height; treat them as
  // content-sized regardless of the per-type registry entry (which covers bar/line).
  if (childBlock.type === "chart" && (childBlock.kind === "progress" || childBlock.kind === "heatmap")) return true;

  const reg = getRegistry();
  if (reg) return reg.getLayoutInfo(childBlock.type).isContentSized;

  // Fallback (during migration)
  if (["text_block", "bullet_list", "section_header"].includes(childBlock.type)) return true;
  if (childBlock.type === "chart" && childBlock.kind === "progress") return true;
  if (childBlock.type === "chart" && childBlock.kind === "heatmap") return true;
  return false;
}

/**
 * Can this child absorb sibling slack in phase 4?
 */
function canStretchInPhase4(childBlock, naturalHeight) {
  // progress/heatmap: content-sized, non-stretchable so gap:"spread" gets the slack.
  if (childBlock.type === "chart" && (childBlock.kind === "progress" || childBlock.kind === "heatmap")) return false;

  const reg = getRegistry();
  if (reg) return reg.getLayoutInfo(childBlock.type).canStretch;

  // Fallback
  if (naturalHeight === null) return true;
  if (childBlock.type === "text_block" && childBlock.slot_layout === "cards") return true;
  return false;
}

/**
 * Measure a child's natural height by rendering with a huge height budget
 * and _measureOnly flag.  Returns null for container-sized children.
 */
function measureNaturalHeight(child, crystalizedData, childWidth, fontScale, theme, debug, chromeFontScale) {
  const HUGE = 99999;
  const mg = renderBlock(child, crystalizedData, {
    x: 0, y: 0, width: childWidth, height: HUGE,
    fontScale, chromeFontScale, theme, debug, isOutermost: false,
    imageCallback: null,
    _measureOnly: true
  });
  const h = mg.height() || 0;
  mg.destroy();
  if (h >= HUGE - 1) return null;
  return h;
}

// ── Main layout function ─────────────────────────────────────────────────

/**
 * Render a flow block — the 5-phase vertical layout algorithm.
 *
 * Phase 1: Measure natural heights of content-sized children.
 * Phase 2: Compute heights according to sizing mode (content/equal_max/proportional).
 * Phase 3: Text shrinking when content overflows (binary search for optimal scale).
 * Phase 4: Expand excess space into stretchable children.
 * Phase 5: Render children with final heights, capping at parent boundary.
 */
export function renderFlow(block, crystalizedData, options) {
  const {
    x = 0, y = 0, width = 800, height = 600,
    imageCallback, theme, fontScale = 1, debug, isOutermost
  } = options;
  const chromeFontScale = options.chromeFontScale ?? fontScale;
  const direction = block.direction || "vertical";
  // ── Debug: trace fontScale through the layout hierarchy ──
  const _fsLabel = (block.id || block.direction || 'flow') + (block.id ? `(${block.id})` : '');
  if (block.id === 'evidence' || block.id === 'financial_col' || block.id === 'financial_activity' || block.id === 'revenue_chart' || block.id === 'synthesis') {
    console.log(`[VizDslLayoutEngine] ${_fsLabel} fontScale=${fontScale} dir=${direction} w=${width} h=${height}`);
  }
  const group = new Konva.Group({ x, y, width, height });
  // A flow's own background always spans the FULL box it was given (not the
  // content-derived height "content" sizing normally reports) — e.g. a
  // full-height panel behind a shorter, vertically-centered column. Content
  // sizing's own height override (below) runs after this and is unaffected.
  if (block.background) {
    group.add(new Konva.Rect({ x: 0, y: 0, width, height, fill: themeColor(theme, block.background, block.background) }));
  }
  const children = selectVisibleChildren(block, crystalizedData);
  const innerOpts = { imageCallback, theme, fontScale, chromeFontScale, debug, isOutermost: false, _preview: options._preview, provenance: options.provenance, renderContextId: options.renderContextId };

  if (direction !== "vertical") {
    // Horizontal — distribute width proportionally
    const gap = block.gap || 0;
    const padX = block.padding_x || 0;
    const usableW = Math.max(0, width - padX * 2 - gap * Math.max(0, children.length - 1));
    const totalProp = children.reduce((sum, c) => sum + (c.proportion || 1), 0);

    // If any child is content-sized and overflows the allocated height,
    // binary-search a local fontScale so text shrinks to fit.  This runs
    // inside the horizontal flow so it doesn't affect the outer layout.
    let localFontScale = fontScale;
    const contentKids = children.filter(c => isContentSized(c));
    if (block.id === 'evidence' || block.id === 'synthesis') {
      console.log(`[VizDslLayoutEngine] HORIZONtal(${block.id || '?'}) contentKids=${contentKids.length} fs=${fontScale}`);
    }
    if (contentKids.length > 0) {
      const kidWidths = contentKids.map(c => ((c.proportion || 1) / totalProp) * usableW);

      const measureOverflow = (fs) => {
        let maxH = 0;
        for (let i = 0; i < contentKids.length; i++) {
          const h = measureNaturalHeight(contentKids[i], crystalizedData, kidWidths[i], fs, theme, debug, chromeFontScale);
          if (h !== null) maxH = Math.max(maxH, h);
        }
        return maxH;
      };

      const contentMaxH = measureOverflow(fontScale);
      if (contentMaxH > 0 && contentMaxH > height + 0.5) {
        let minAllowed = 0;
        for (const c of contentKids) {
          if (c.min_font_scale != null) minAllowed = Math.max(minAllowed, c.min_font_scale);
        }

        let lo = minAllowed, hi = 1.0, bestFactor = 1.0;
        const MAX_ITER = 5;
        for (let iter = 0; iter < MAX_ITER; iter++) {
          const mid = (lo + hi) / 2;
          const trialH = measureOverflow(fontScale * mid);
          if (trialH <= height) {
            bestFactor = mid;
            if (mid >= 0.99) break;
            lo = mid;
          } else {
            hi = mid;
          }
        }
        localFontScale = Math.max(0.05, fontScale * bestFactor);
        console.log(`[VizDslLayoutEngine] HORIZONTAL SHRINK(${block.id || '?'}) bestFactor=${bestFactor} localFontScale=${localFontScale} height=${height}`);
      }
    }

    // Render all children at the (possibly shrunk) scale
    const renderOpts = { ...innerOpts, fontScale: localFontScale };
    let cx = padX;
    for (let ci = 0; ci < children.length; ci++) {
      const child = children[ci];
      const childWidth = ((child.proportion || 1) / totalProp) * usableW;
      // Support vertical_align for content-sized children: measure their natural
      // height and offset y so they center / bottom-align within the parent height.
      let childY = 0;
      if (isContentSized(child) && child.vertical_align && height < 99999) {
        const natH = measureNaturalHeight(child, crystalizedData, childWidth, localFontScale, theme, debug, chromeFontScale) ?? 0;
        const slack = height - natH;
        console.log(`[H-ALIGN] ${child.type}/${child.id} sizing=${child.sizing} va=${child.vertical_align} natH=${natH} parentH=${height} slack=${slack}`);
        if (slack > 0) {
          childY = child.vertical_align === "middle" ? Math.round(slack / 2)
                 : child.vertical_align === "bottom" ? Math.round(slack)
                 : 0;
        }
      }
      const childGroup = renderBlock(child, crystalizedData, { ...renderOpts, x: cx, y: childY, width: childWidth, height });
      group.add(childGroup);
      cx += childWidth;
      if (ci < children.length - 1) cx += gap;
    }

    group.clip({ x: 0, y: 0, width, height });
    return group;
  }

  // ── Vertical flow: 5-phase layout ──────────────────────────────────
  const sizing = block.sizing || "content";
  const expand = block.expand || "share";
  const totalProp = children.reduce((sum, c) => sum + (c.proportion || 1), 0);

  // Optional padding insets this flow's children (x/width and the layout
  // height budget) without shrinking the background rect above, which
  // still covers the full given box regardless. Lets a flow provide ONE
  // consistent inset for its children instead of each child managing its
  // own padding/margin separately (and disagreeing with its siblings).
  const padRaw = block.padding;
  const padIsObj = padRaw !== null && typeof padRaw === "object";
  const padLeftV = Math.round((padIsObj ? (padRaw.left ?? 0) : (padRaw || 0)) * fontScale);
  const padRightV = Math.round((padIsObj ? (padRaw.right ?? 0) : (padRaw || 0)) * fontScale);
  const padTopV = Math.round((padIsObj ? (padRaw.top ?? 0) : (padRaw || 0)) * fontScale);
  const padBottomV = Math.round((padIsObj ? (padRaw.bottom ?? 0) : (padRaw || 0)) * fontScale);
  const contentWidth = Math.max(0, width - padLeftV - padRightV);
  const contentHeight = Math.max(0, height - padTopV - padBottomV);

  // Phase 1: measure natural heights
  const naturalHeights = children.map(child =>
    isContentSized(child)
      ? measureNaturalHeight(child, crystalizedData, contentWidth, fontScale, theme, debug, chromeFontScale)
      : null
  );

  // Phase 2: compute heights
  let allocated;
  if (sizing === "proportional") {
    allocated = children.map(c => ((c.proportion || 1) / totalProp) * contentHeight);
  } else if (sizing === "equal_max") {
    const contentHeights = naturalHeights.filter(h => h !== null);
    const propHeights = children.map((c, i) =>
      naturalHeights[i] !== null ? null : ((c.proportion || 1) / totalProp) * contentHeight
    ).filter(h => h !== null);
    const allHeights = [...contentHeights, ...propHeights];
    const maxH = allHeights.length > 0 ? Math.max(...allHeights, 1) : contentHeight / Math.max(children.length, 1);
    allocated = children.map((c, i) =>
      naturalHeights[i] !== null ? maxH : ((c.proportion || 1) / totalProp) * contentHeight
    );
  } else {
    allocated = children.map((c, i) =>
      naturalHeights[i] !== null ? naturalHeights[i] : ((c.proportion || 1) / totalProp) * contentHeight
    );
  }

  let totalAllocated = allocated.reduce((a, b) => a + b, 0);

  // Phase 3: text shrinking. Only enter if there are content-sized children with
  // finite natural heights — those are the only ones whose allocation decreases as
  // font scale drops. If all naturalHeights are null (all children are proportional /
  // container-sized), remeasureAt always returns the same total and the binary search
  // produces no valid shrink, causing the fallback to crush finalFontScale to 0.05.
  // The sub-pixel epsilon also guards floating-point rounding (0.4+0.08+0.52 in JS
  // equals 1.0000000000000002, so proportional totals land microscopically above height).
  const hasContentSizedChild = naturalHeights.some(h => h !== null);
  let finalFontScale = fontScale;
  const _outerId = block.id || '(outer)';
  if (['evidence', 'synthesis', 'financial_col', 'financial_activity', '(outer)'].includes(_outerId)) {
    console.log(`[VizDslLayoutEngine] VERT(${_outerId}) hasContentSized=${hasContentSizedChild} totalAlloc=${totalAllocated} height=${contentHeight} naturalHeights=[${naturalHeights.join(',')}]
      enterPhase3=${hasContentSizedChild && totalAllocated > contentHeight + 0.01} fontScale=${fontScale}`);
  }
  if (hasContentSizedChild && totalAllocated > contentHeight + 0.01) {
    let minAllowed = 0;
    for (const child of children) {
      if (child.type === "text_block" && child.min_font_scale != null) {
        minAllowed = Math.max(minAllowed, child.min_font_scale);
      }
    }

    const remeasureAt = (shrink) => {
      const scale = fontScale * shrink;
      if (sizing === "proportional") {
        const newAlloc = children.map((c, i) => {
          const propH = ((c.proportion || 1) / totalProp) * contentHeight;
          if (isContentSized(c)) {
            const natH = measureNaturalHeight(c, crystalizedData, contentWidth, scale, theme, debug, chromeFontScale) ?? 0;
            return Math.max(Math.min(natH, propH), 1);
          }
          return propH;
        });
        return { newAlloc, newTotal: newAlloc.reduce((a, b) => a + b, 0), scale };
      }
      const newAlloc = children.map((c, i) => {
        if (naturalHeights[i] !== null) {
          const natH = measureNaturalHeight(c, crystalizedData, contentWidth, scale, theme, debug, chromeFontScale);
          return natH !== null ? natH : naturalHeights[i] * shrink;
        }
        return ((c.proportion || 1) / totalProp) * contentHeight;
      });
      return { newAlloc, newTotal: newAlloc.reduce((a, b) => a + b, 0), scale };
    };

    let shrink = Math.max(contentHeight / totalAllocated, minAllowed);
    let bestShrink = null, bestAttempt = null;
    let lo = minAllowed, hi = 1.0;
    const MAX_ITER = 5;

    for (let iter = 0; iter < MAX_ITER; iter++) {
      const trial = remeasureAt(shrink);
      if (trial.newTotal <= contentHeight) {
        bestShrink = shrink; bestAttempt = trial;
        if (shrink >= 1.0 - 0.01) break;
        lo = shrink; shrink = Math.min((shrink + hi) / 2, 1.0);
      } else {
        hi = shrink;
        if (shrink <= minAllowed + 0.01) break;
        shrink = Math.max((lo + shrink) / 2, minAllowed);
      }
    }

    if (bestAttempt === null) {
      // Shrinking never made content fit. Check whether shrinking actually
      // reduces the total — if not (e.g. proportional mode with content-sized
      // children below their proportional cap), shrinking only crushes fontScale
      // without benefit. Bail out and keep the original scale.
      const worstTrial = remeasureAt(minAllowed);
      if (Math.abs(worstTrial.newTotal - totalAllocated) < 1) {
        // Shrinking has no measurable effect on total height — skip it.
        if (block.id) console.log(`[VizDslLayoutEngine] SKIP SHRINK(${block.id}) total unchanged ${totalAllocated}→${worstTrial.newTotal}, keeping fontScale=${fontScale}`);
        bestAttempt = { newAlloc: allocated, newTotal: totalAllocated, scale: fontScale };
        bestShrink = 1;
      } else {
        bestAttempt = worstTrial;
        bestShrink = minAllowed;
      }
    }

    finalFontScale = Math.max(0.05, bestAttempt.scale);
    if (!Number.isFinite(finalFontScale)) finalFontScale = fontScale;
    allocated = bestAttempt.newAlloc;
    totalAllocated = bestAttempt.newTotal;
    if (block.id === 'evidence' || block.id === 'synthesis' || block.id === 'financial_col' || block.id === 'financial_activity') {
      console.log(`[VizDslLayoutEngine] VERTICAL SHRINK(${block.id || '?'}) bestShrink=${bestShrink} finalFontScale=${finalFontScale} height=${contentHeight} totalAlloc=${totalAllocated}`);
    }
  }

  // A flow can ask to center its children as a group in the leftover space
  // instead of stretching into it — e.g. a fixed-height panel whose content
  // (title + list) is naturally shorter than the panel. Only meaningful
  // during real rendering: `height` is a HUGE sentinel while measuring, and
  // centering against it would push every child far off-screen.
  // Deliberately a DIFFERENT field from `vertical_align` — that one is read
  // by a HORIZONTAL parent to position this flow as a whole child (see
  // isContentSized/child.vertical_align above). `content_align` instead
  // centers THIS flow's own children within the height it was given
  // (e.g. a full-height background panel whose header+list are shorter
  // than the panel). Reusing `vertical_align` here would hijack every flow
  // that already sets it for the parent-consumed purpose.
  const centerContent = block.content_align === "middle" && sizing === "content" && !options._measureOnly;

  // Phase 4: expand excess
  const slack = contentHeight - totalAllocated;
  if (slack > 0 && expand === "share" && !centerContent) {
    const stretchIdxs = children
      .map((c, i) => canStretchInPhase4(c, naturalHeights[i]) ? i : -1)
      .filter(i => i >= 0);
    if (stretchIdxs.length > 0) {
      const stretchTotalProp = stretchIdxs.reduce((sum, i) => sum + (children[i].proportion || 1), 0);
      for (const i of stretchIdxs) {
        allocated[i] += slack * (children[i].proportion || 1) / stretchTotalProp;
      }
    }
  }

  // Phase 5: render
  const sumAfterPhase4 = allocated.reduce((a, b) => a + b, 0);
  let interItemGap = 0;
  if (block.gap === "spread" && children.length > 1 && sumAfterPhase4 < contentHeight) {
    interItemGap = (contentHeight - sumAfterPhase4) / (children.length - 1);
  }
  // Numeric gap: use as fixed spacing between children
  const numericGap = typeof block.gap === "number" ? block.gap : 0;

  const renderOpts = { ...innerOpts, fontScale: finalFontScale };
  let cy = padTopV;
  if (centerContent) {
    const contentTotal = allocated.reduce((a, b) => a + b, 0) + (interItemGap || numericGap) * Math.max(0, children.length - 1);
    cy = padTopV + Math.max(0, Math.round((contentHeight - contentTotal) / 2));
  }
  for (let i = 0; i < children.length; i++) {
    const remaining = Math.max(0, contentHeight - (cy - padTopV));
    const childH = Math.min(allocated[i], remaining);
    if (childH <= 0) continue;
    const childGroup = renderBlock(children[i], crystalizedData, {
      ...renderOpts, x: padLeftV, y: cy, width: contentWidth, height: childH
    });
    group.add(childGroup);
    cy += childH;
    if (i < children.length - 1) cy += (interItemGap || numericGap);
  }

  // For content-sized flows, override the group height to the accumulated
  // natural content height (including padding) so measureNaturalHeight reads
  // the correct value when this flow is a child of a horizontal flow
  // (enabling vertical_align).
  if (sizing === "content" && options._measureOnly) {
    group.height(cy + padBottomV);
  }

  group.clip({ x: 0, y: 0, width, height });
  return group;
}

export {
  childHasData,
  selectVisibleChildren,
  isContentSized,
  canStretchInPhase4,
  measureNaturalHeight,
};
